import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';

import Select from '../../../components/Select';
import GenericCategoryGraph, { chartTypeEnum } from '../../../components/Graphs/GenericCategoryGraph';
import { graphContainer } from '../../Dashboards/Widgets/graphContainers';
import { manipulateColor } from '../../../components/Graphs/CompositeGraph/ColorTools';
import TableWithLocalPagination, { Column } from '../../../containers/Table/TableWithLocalPagination';
import { roundToDecimal } from '../../../utils/unitConverters';

export const OPERATIONAL_DASHBOARD_ID = 'BusinessReportOperationalDashboard_1';
export const PERFORMANCE_DASHBOARD_ID = 'BusinessReportPerformanceDashboard_1';

export const OPERATIONAL_WIDGET_DEFINITIONS = [
  { value: 'new_patients', label: 'New patients created', description: 'Patients created in the selected period.', splitBy: null },
  { value: 'enrollments', label: 'Patients enrolled', description: 'Transitions to enrolled status in the selected period.', splitBy: 'program' },
  { value: 'disenrollments', label: 'Disenrollments count', description: 'Transitions to ended status in the selected period.', splitBy: 'program' },
  {
    value: 'disenrollment_reasons',
    label: 'Disenrollment reasons',
    description: 'Disenrollments split by terminated reason.',
    splitBy: 'reason',
  },
  { value: 'kit_orders', label: 'Direct kit orders placed', description: 'Kits ordered through direct shipments in the selected period.', splitBy: 'program' },
  { value: 'device_orders', label: 'Direct device orders placed', description: 'Devices ordered through direct shipments in the selected period.', splitBy: 'program' },
  {
    value: 'shipped_to_activated',
    label: 'Avg days from shipment to activation (Direct)',
    description: 'Average time between shipment and activation for direct shipments completed in the selected period.',
    splitBy: 'program',
  },
  {
    value: 'bulk_orders_table',
    label: 'Bulk orders in period',
    description: 'Bulk orders that were placed or shipped in the selected period, sorted by ordered time.',
    splitBy: null,
    isTable: true,
    hiddenByDefault: false,
    defProps: {
      w: 12,
      h: 8,
      minW: 6,
      minH: 6,
    },
  },
];

export const PERFORMANCE_WIDGET_DEFINITIONS = [
  {
    value: 'onboarded_to_registered',
    label: 'Transition Onboarded → Registered',
    description: 'Average time (days) of moving patient from onboarded to registered state.',
    splitBy: null,
  },
  {
    value: 'registered_to_enrolled',
    label: 'Transition Registered → Enrolled',
    description: 'Average time (days) of moving patient from registered to enrolled state.',
    splitBy: null,
  },
  {
    value: 'enrolled_to_activated',
    label: 'Transition Enrolled → Activated',
    description: 'Average time (days) of moving patient from enrolled to activated state.',
    splitBy: null,
  },
  {
    value: 'average_kit_usage',
    label: 'Average Kit Usage',
    description:
      'Shows the average usage across kits assigned to active enrollments in the selected period. A kit is counted as active for a given day if it has at least one measurement on that day.',
    splitBy: null,
  },
  {
    value: 'average_device_usage',
    label: 'Average Device Usage',
    description:
      'Shows the average usage across devices assigned to active enrollments in the selected period. A device is counted as active for a given day if it has at least one measurement on that day.',
    splitBy: null,
  },
];

const BASE_RANGE_OPTIONS = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
];

const GROUP_BY_OPTIONS = [
  { value: 'none', label: 'No grouping' },
  { value: 'client', label: 'Client' },
  { value: 'partner', label: 'Partner' },
  { value: 'provider', label: 'Provider' },
];

const MAX_ORGANIZATIONS_PER_WIDGET = 10;
const AVERAGE_METRICS = new Set([
  'shipped_to_activated',
  'onboarded_to_registered',
  'registered_to_enrolled',
  'enrolled_to_activated',
  'average_kit_usage',
  'average_device_usage',
]);
const GROUP_SHADED_SPLIT_METRICS = new Set([
  'enrollments',
  'disenrollments',
  'disenrollment_reasons',
  'kit_orders',
  'device_orders',
]);

const ALL_WIDGET_DEFINITIONS = [...OPERATIONAL_WIDGET_DEFINITIONS, ...PERFORMANCE_WIDGET_DEFINITIONS];
const PERFORMANCE_METRICS = new Set(PERFORMANCE_WIDGET_DEFINITIONS.map(metric => metric.value));
const getMetricDefinition = metricValue => ALL_WIDGET_DEFINITIONS.find(metric => metric.value === metricValue);
const isAverageMetric = metricValue => AVERAGE_METRICS.has(metricValue);
const hasSplit = metricValue => !!getMetricDefinition(metricValue)?.splitBy;
const usesStackedSplit = metricValue => hasSplit(metricValue) && !isAverageMetric(metricValue);
const usesGroupShadedSplit = metricValue => GROUP_SHADED_SPLIT_METRICS.has(metricValue);
const isTableMetric = metricValue => !!getMetricDefinition(metricValue)?.isTable;

const getDefaultChartType = metric =>
  usesStackedSplit(metric) ? chartTypeEnum.horizontalStackedBar : chartTypeEnum.horizontalBar;

const getChartTypeOptions = metric =>
  usesStackedSplit(metric)
    ? [
        { value: chartTypeEnum.horizontalStackedBar, label: 'Horizontal stacked bar' },
        { value: chartTypeEnum.stackedBar, label: 'Stacked bar' },
      ]
    : hasSplit(metric)
      ? [
          { value: chartTypeEnum.horizontalBar, label: 'Horizontal grouped bar' },
          { value: chartTypeEnum.bar, label: 'Grouped bar' },
        ]
      : [
          { value: chartTypeEnum.horizontalBar, label: 'Horizontal bar' },
          { value: chartTypeEnum.bar, label: 'Bar' },
          { value: chartTypeEnum.pie, label: 'Pie' },
        ];

const getOperationalChartTypeConfiguration = metric =>
  isTableMetric(metric)
    ? {}
    : {
        chartType: {
          type: 'select',
          name: 'chartType',
          label: 'Chart Type',
          defaultValue: getDefaultChartType(metric),
          options: getChartTypeOptions(metric),
          canBeSetByDashboard: false,
        },
      };

const getMetricDefProps = metric => ({
  i: metric.value,
  w: metric.defProps?.w || 6,
  h: metric.defProps?.h || 6,
  minW: metric.defProps?.minW || 4,
  minH: metric.defProps?.minH || 4,
});

const buildOperationalLayout = (widgets, widthResolver, gridWidth) => {
  let currentX = 0;
  let currentY = 0;
  let currentRowHeight = 0;

  return widgets.map(widget => {
    const itemWidth = Math.min(widthResolver(widget), gridWidth);
    const itemHeight = widget.defProps.h;

    if (currentX > 0 && currentX + itemWidth > gridWidth) {
      currentY += currentRowHeight;
      currentX = 0;
      currentRowHeight = 0;
    }

    const layoutItem = {
      ...widget.defProps,
      x: currentX,
      y: currentY,
      w: itemWidth,
    };

    currentX += itemWidth;
    currentRowHeight = Math.max(currentRowHeight, itemHeight);

    if (currentX >= gridWidth) {
      currentY += currentRowHeight;
      currentX = 0;
      currentRowHeight = 0;
    }

    return layoutItem;
  });
};

export const getOperationalWidgetMetadata = () =>
  OPERATIONAL_WIDGET_DEFINITIONS.map((metric, index) => ({
    id: metric.value,
    name: metric.label,
    description: metric.description,
    group: 'Operational',
    permission: '',
    defProps: getMetricDefProps(metric),
    hiddenByDefault: metric.hiddenByDefault ?? index > 7,
    configuration: getOperationalChartTypeConfiguration(metric.value),
  }));

export const getPerformanceWidgetMetadata = () =>
  PERFORMANCE_WIDGET_DEFINITIONS.map(metric => ({
    id: metric.value,
    name: metric.label,
    description: metric.description,
    group: 'Performance',
    permission: '',
    defProps: getMetricDefProps(metric),
    hiddenByDefault: false,
    configuration: {
      chartType: {
        ...getOperationalChartTypeConfiguration(metric.value).chartType,
        defaultValue: chartTypeEnum.bar,
      },
    },
  }));

const getOperationalL = () => {
  const widgets = getOperationalWidgetMetadata();
  return buildOperationalLayout(widgets, widget => widget.defProps.w, 12);
};

const getOperationalM = () => {
  const widgets = getOperationalWidgetMetadata();
  return buildOperationalLayout(widgets, () => 8, 8);
};

const getOperationalS = () => {
  const widgets = getOperationalWidgetMetadata();
  return buildOperationalLayout(widgets, () => 4, 4);
};

export const getOperationalDefaultLayouts = () => ({
  l: getOperationalL(),
  m: getOperationalM(),
  s: getOperationalS(),
});

export const getPerformanceDefaultLayouts = () => ({
  l: PERFORMANCE_WIDGET_DEFINITIONS.map((metric, index) => ({
    ...getMetricDefProps(metric),
    x: 0,
    y: index * 5,
    w: 12,
    h: 5,
  })),
  m: PERFORMANCE_WIDGET_DEFINITIONS.map((metric, index) => ({
    ...getMetricDefProps(metric),
    x: 0,
    y: index * 5,
    w: 8,
    h: 5,
  })),
  s: PERFORMANCE_WIDGET_DEFINITIONS.map((metric, index) => ({
    ...getMetricDefProps(metric),
    x: 0,
    y: index * 6,
    w: 4,
    h: 6,
  })),
});

const formatMetricValue = (metric, value) => {
  if (value == null || Number.isNaN(value)) {
    return '-';
  }
  if (
    metric === 'shipped_to_activated' ||
    metric === 'onboarded_to_registered' ||
    metric === 'registered_to_enrolled' ||
    metric === 'enrolled_to_activated'
  ) {
    return `${value.toFixed(2)} d`;
  }
  if (metric === 'average_kit_usage' || metric === 'average_device_usage') {
    return `${value.toFixed(2)} %`;
  }
  return value.toLocaleString();
};

const formatMetricCountLabel = (metric, count) => {
  if (!count) {
    return '';
  }
  if (metric === 'shipped_to_activated') return `${count} shipment${count === 1 ? '' : 's'}`;
  if (
    metric === 'onboarded_to_registered' ||
    metric === 'registered_to_enrolled' ||
    metric === 'enrolled_to_activated'
  ) {
    return `${count} transition${count === 1 ? '' : 's'}`;
  }
  if (metric === 'average_kit_usage' || metric === 'average_device_usage') {
    return `${count} enrollment${count === 1 ? '' : 's'}`;
  }
  if (metric === 'kit_orders') return `${count} kit${count === 1 ? '' : 's'}`;
  if (metric === 'device_orders') return `${count} device${count === 1 ? '' : 's'}`;
  if (metric === 'enrollments') {
    return `${count} enrollment${count === 1 ? '' : 's'}`;
  }
  if (metric === 'disenrollments' || metric === 'disenrollment_reasons') {
    return `${count} disenrollment${count === 1 ? '' : 's'}`;
  }
  if (metric === 'new_patients') return `${count} patient${count === 1 ? '' : 's'}`;
  return `${count}`;
};

const getProgramLabel = scope => scope?.program || 'No program';
const getReasonLabel = reason => reason || 'Unknown reason';
const isBulkShipment = shipment => !shipment?.patient_id && !shipment?.enrollment_id;
const isDirectShipment = shipment => !isBulkShipment(shipment);
const formatOperationalTimestamp = value => (value ? moment(value).format('MMM D, YYYY h:mm A') : '-');
const formatOperationalDays = hours => (hours == null || Number.isNaN(hours) ? '-' : `${(hours / 24).toFixed(1)} d`);
const getOrganizationScope = scope => {
  if (!scope?.organization_id) {
    return null;
  }

  return {
    id: scope.organization_id,
    label: scope.organization_name || scope.organization_id,
  };
};

const getGroupScope = (scope, groupBy) => {
  if (groupBy === 'none') {
    return getOrganizationScope(scope);
  }

  if (!scope) {
    return null;
  }

  if (groupBy === 'client') {
    if (!scope.client_organization_id) {
      return null;
    }
    return {
      id: scope.client_organization_id,
      label: scope.client_organization_name || scope.client_organization_id,
    };
  }

  if (groupBy === 'partner') {
    if (!scope.partner_organization_id) {
      return null;
    }
    return {
      id: scope.partner_organization_id,
      label: scope.partner_organization_name || scope.partner_organization_id,
    };
  }

  if (groupBy === 'provider') {
    if (!scope.provider_id) {
      return null;
    }
    return {
      id: scope.provider_id,
      label: scope.provider_name || scope.provider_id,
    };
  }

  return getOrganizationScope(scope);
};

const addRecordToGroups = (groupsMap, scope, groupBy, value, splitLabel = null) => {
  const groupScope = getGroupScope(scope, groupBy);
  if (!groupScope) {
    return;
  }

  if (!groupsMap.has(groupScope.id)) {
    groupsMap.set(groupScope.id, {
      key: groupScope.id,
      label: groupScope.label,
      total: 0,
      records: 0,
      splits: {},
    });
  }

  const group = groupsMap.get(groupScope.id);
  group.total += value;
  group.records += 1;

  if (splitLabel) {
    if (!group.splits[splitLabel]) {
      group.splits[splitLabel] = {
        total: 0,
        records: 0,
      };
    }
    group.splits[splitLabel].total += value;
    group.splits[splitLabel].records += 1;
  }
};

const buildMetricChartModel = (dataset, metric, colorPalette, groupBy) => {
  const groupsMap = new Map();
  const metricDefinition = getMetricDefinition(metric);
  if (!dataset) {
    return { chartData: [], dataSeries: [] };
  }

  if (metric === 'new_patients') {
    (dataset.patients || []).forEach(patient => {
      addRecordToGroups(groupsMap, patient.scope, groupBy, 1);
    });
  }

  if (metric === 'enrollments') {
    (dataset.transitions || [])
      .filter(transition => transition.new_status === 'enrolled')
      .forEach(transition => {
        addRecordToGroups(groupsMap, transition.scope, groupBy, 1, getProgramLabel(transition.scope));
      });
  }

  if (metric === 'disenrollments') {
    (dataset.transitions || [])
      .filter(transition => transition.new_status === 'ended')
      .forEach(transition => {
        addRecordToGroups(groupsMap, transition.scope, groupBy, 1, getProgramLabel(transition.scope));
      });
  }

  if (metric === 'disenrollment_reasons') {
    (dataset.transitions || [])
      .filter(transition => transition.new_status === 'ended')
      .forEach(transition => {
        addRecordToGroups(groupsMap, transition.scope, groupBy, 1, getReasonLabel(transition.reason));
      });
  }

  if (metric === 'kit_orders') {
    (dataset.shipments || [])
      .filter(shipment => isDirectShipment(shipment) && shipment.ordered_in_period && shipment.kit_count > 0)
      .forEach(shipment => {
        addRecordToGroups(groupsMap, shipment.scope, groupBy, shipment.kit_count || 0, getProgramLabel(shipment.scope));
      });
  }

  if (metric === 'device_orders') {
    (dataset.shipments || [])
      .filter(shipment => isDirectShipment(shipment) && shipment.ordered_in_period && shipment.device_count > 0)
      .forEach(shipment => {
        addRecordToGroups(groupsMap, shipment.scope, groupBy, shipment.device_count || 0, getProgramLabel(shipment.scope));
      });
  }

  if (metric === 'shipped_to_activated') {
    (dataset.shipments || [])
      .filter(shipment => isDirectShipment(shipment) && shipment.activated_in_period && shipment.shipped_to_activated_hours != null)
      .forEach(shipment => {
        addRecordToGroups(
          groupsMap,
          shipment.scope,
          groupBy,
          roundToDecimal(shipment.shipped_to_activated_hours / 24, 2),
          getProgramLabel(shipment.scope),
        );
      });
  }

  if (metric === 'onboarded_to_registered') {
    (dataset.transitions || [])
      .filter(transition => transition.previous_status === 'onboarded' && transition.new_status === 'verified' && transition.duration_hours != null)
      .forEach(transition => {
        addRecordToGroups(groupsMap, transition.scope, groupBy, roundToDecimal(transition.duration_hours / 24, 2));
      });
  }

  if (metric === 'registered_to_enrolled') {
    (dataset.transitions || [])
      .filter(transition => transition.previous_status === 'verified' && transition.new_status === 'enrolled' && transition.duration_hours != null)
      .forEach(transition => {
        addRecordToGroups(groupsMap, transition.scope, groupBy, roundToDecimal(transition.duration_hours / 24, 2));
      });
  }

  if (metric === 'enrolled_to_activated') {
    (dataset.transitions || [])
      .filter(transition => transition.previous_status === 'enrolled' && transition.new_status === 'activated' && transition.duration_hours != null)
      .forEach(transition => {
        addRecordToGroups(groupsMap, transition.scope, groupBy, roundToDecimal(transition.duration_hours / 24, 2));
      });
  }

  if (metric === 'average_kit_usage') {
    (dataset.usage || []).forEach(usage => {
      addRecordToGroups(groupsMap, usage.scope, groupBy, roundToDecimal(usage.kit_usage_percent || 0, 2));
    });
  }

  if (metric === 'average_device_usage') {
    (dataset.usage || [])
      .filter(usage => usage.device_usage_percent != null)
      .forEach(usage => {
        addRecordToGroups(groupsMap, usage.scope, groupBy, roundToDecimal(usage.device_usage_percent, 2));
      });
  }

  const totalStats = Array.from(groupsMap.values()).reduce(
    (acc, group) => ({
      total: acc.total + group.total,
      records: acc.records + group.records,
    }),
    { total: 0, records: 0 },
  );

  const totalSplitStats = Array.from(groupsMap.values()).reduce((acc, group) => {
    Object.entries(group.splits || {}).forEach(([splitValue, stats]) => {
      if (!acc[splitValue]) {
        acc[splitValue] = { total: 0, records: 0 };
      }
      acc[splitValue].total += stats.total;
      acc[splitValue].records += stats.records;
    });
    return acc;
  }, {});

  const rows = Array.from(groupsMap.values())
    .map(group => ({
      key: group.key,
      label: group.label,
      count: group.records,
      value: isAverageMetric(metric) && group.records > 0 ? group.total / group.records : group.total,
      splits: Object.fromEntries(
        Object.entries(group.splits || {}).map(([splitValue, stats]) => [
          splitValue,
          isAverageMetric(metric) && stats.records > 0 ? stats.total / stats.records : stats.total,
        ]),
      ),
      splitCounts: Object.fromEntries(
        Object.entries(group.splits || {}).map(([splitValue, stats]) => [splitValue, stats.records]),
      ),
    }))
    .filter(row => row.count > 0 && row.value != null && !Number.isNaN(row.value))
    .sort((a, b) => b.value - a.value || a.label.localeCompare(b.label))
    .slice(0, MAX_ORGANIZATIONS_PER_WIDGET);

  if (!metricDefinition?.splitBy) {
    const chartRows = [
      ...(totalStats.records > 0
        ? [{
            label: 'Total',
            value: isAverageMetric(metric) ? totalStats.total / totalStats.records : totalStats.total,
            totalCount: totalStats.records,
          }]
        : []),
      ...rows.map(row => ({ label: row.label, value: row.value, totalCount: row.count })),
    ];
    return {
      chartData: chartRows,
      dataSeries: [],
    };
  }

  const splitLabels = Object.entries(totalSplitStats)
    .sort((a, b) => {
      const aValue = isAverageMetric(metric) && a[1].records > 0 ? a[1].total / a[1].records : a[1].total;
      const bValue = isAverageMetric(metric) && b[1].records > 0 ? b[1].total / b[1].records : b[1].total;
      return bValue - aValue || a[0].localeCompare(b[0]);
    })
    .map(([splitValue]) => splitValue);

  const chartRows = [
    ...(totalStats.records > 0
      ? [{
          label: 'Total',
          ...Object.fromEntries(
            splitLabels.map(splitValue => [
              splitValue,
              isAverageMetric(metric) && totalSplitStats[splitValue]?.records > 0
                ? totalSplitStats[splitValue].total / totalSplitStats[splitValue].records
                : totalSplitStats[splitValue]?.total || 0,
            ]),
          ),
          splitCounts: Object.fromEntries(
            splitLabels.map(splitValue => [splitValue, totalSplitStats[splitValue]?.records || 0]),
          ),
          totalCount: totalStats.records,
        }]
      : []),
    ...rows.map((row, rowIndex) => {
      const baseColor = colorPalette?.interpolate?.((rowIndex + 1) / (rows.length + 1)) || '#77A0D9';
      return {
        label: row.label,
        ...Object.fromEntries(splitLabels.map(splitValue => [splitValue, row.splits?.[splitValue] || 0])),
        splitCounts: Object.fromEntries(splitLabels.map(splitValue => [splitValue, row.splitCounts?.[splitValue] || 0])),
        totalCount: row.count,
        cellColors: Object.fromEntries(
          splitLabels.map((splitValue, splitIndex) => [
            splitValue,
            manipulateColor(Math.min(0.55, splitIndex * 0.14), baseColor, '#ffffff', true) || baseColor,
          ]),
        ),
      };
    }),
  ];

  return {
    chartData: chartRows,
    dataSeries: splitLabels,
  };
};

function BulkOrdersTableWidget({ metric, operationalFacts }) {
  const tableData = useMemo(
    () =>
      (operationalFacts?.shipments || [])
        .filter(shipment => isBulkShipment(shipment) && (shipment.ordered_in_period || shipment.shipped_in_period))
        .map(shipment => ({
          shipmentId: shipment.shipment_id,
          orderedAtTs: moment(shipment.ordered_at).valueOf(),
          orderedAtLabel: formatOperationalTimestamp(shipment.ordered_at),
          destination: shipment.scope?.organization_name || shipment.scope?.organization_id || '-',
          kitCount: shipment.kit_count || 0,
          deviceCount: shipment.device_count || 0,
          shippedAtTs: shipment.shipped_at ? moment(shipment.shipped_at).valueOf() : -1,
          shippedAtLabel: shipment.shipped_at ? formatOperationalTimestamp(shipment.shipped_at) : 'In progress',
          fulfillmentHours: shipment.order_to_shipped_hours ?? -1,
          fulfillmentLabel: shipment.shipped_at ? formatOperationalDays(shipment.order_to_shipped_hours) : 'In progress',
        }))
        .sort((a, b) => b.orderedAtTs - a.orderedAtTs),
    [operationalFacts],
  );

  const columns = useMemo(
    () => [
      <Column key="shipmentId" title="Shipment ID" value={row => row.shipmentId} sortKey="shipmentId" />,
      <Column key="orderedAt" title="Ordered at" value={row => row.orderedAtLabel} sortKey="orderedAtTs" />,
      <Column key="destination" title="Destination" value={row => row.destination} sortKey="destination" />,
      <Column key="kits" title="Kits" value={row => row.kitCount} sortKey="kitCount" />,
      <Column key="devices" title="Devices" value={row => row.deviceCount} sortKey="deviceCount" />,
      <Column key="shippedAt" title="Shipped at" value={row => row.shippedAtLabel} sortKey="shippedAtTs" />,
      <Column key="fulfillment" title="Fulfillment" value={row => row.fulfillmentLabel} sortKey="fulfillmentHours" />,
    ],
    [],
  );

  return graphContainer(
    tableData.length === 0 ? (
      <div className="report-operational-empty report-operational-empty-inline">
        No bulk orders for selected period and filters.
      </div>
    ) : (
      <div className="report-operational-table-host">
        <TableWithLocalPagination
          className="report-operational-bulk-orders-table"
          name="report-operational-bulk-orders"
          uuid="68b44764-0c2b-4f50-b00a-3d5c5ecf6ed9"
          data={tableData}
          columns={columns}
          pageLimit={8}
        />
      </div>
    ),
    metric.label,
    metric.description,
  );
}

BulkOrdersTableWidget.propTypes = {
  metric: PropTypes.shape({
    label: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
  }).isRequired,
  operationalFacts: PropTypes.object,
};

function OperationalMetricWidget({ metric, operationalFacts, colorPalette, config, groupBy }) {
  const chartType = config?.chartType?.value || getDefaultChartType(metric.value);
  const widgetModel = useMemo(
    () => buildMetricChartModel(operationalFacts, metric.value, colorPalette, groupBy),
    [metric.value, operationalFacts, colorPalette, groupBy],
  );
  const chartData = widgetModel.chartData || [];
  const chartDataByLabel = useMemo(
    () => Object.fromEntries(chartData.map(item => [item.label, item])),
    [chartData],
  );
  const dataSeries = hasSplit(metric.value)
    ? (widgetModel.dataSeries || []).map((seriesName, index, series) => ({
        name: seriesName,
        color: colorPalette?.interpolate?.((index + 1) / (series.length + 1)) || '#77A0D9',
        colorSelector: usesGroupShadedSplit(metric.value)
          ? item => item.cellColors?.[seriesName]
          : undefined,
        selector: item => item[seriesName] || 0,
      }))
    : [{
        name: metric.label,
        color:
          colorPalette?.interpolate?.(
            (ALL_WIDGET_DEFINITIONS.findIndex(item => item.value === metric.value) + 1) /
              (ALL_WIDGET_DEFINITIONS.length + 1),
          ) || '#77A0D9',
        selector: item => item.value,
      }];

  const content = chartData.length === 0 ? (
    <div className="report-operational-empty report-operational-empty-inline">
      No data for selected period and filters.
    </div>
  ) : (
    <div className="report-operational-graph-host">
      <GenericCategoryGraph
        graphData={chartData}
        hasBorder
        forceCondensed
        xAxisTopPosition={false}
        yAxisUnit=""
        yAxisWidth={
          chartType === chartTypeEnum.horizontalBar || chartType === chartTypeEnum.horizontalStackedBar
            ? 220
            : 72
        }
        tooltipTitle={metric.label}
        graphType={chartType}
        colorPalette={colorPalette}
        categorySelector={item => item.label}
        labelFormatter={(category, seriesIndex, value) => {
          const seriesName = dataSeries?.[seriesIndex]?.name || metric.label;
          const categoryData = chartDataByLabel[category];
          const count = hasSplit(metric.value) ? categoryData?.splitCounts?.[seriesName] : categoryData?.totalCount;
          return {
            lines: [
              isAverageMetric(metric.value) && count
                ? `${seriesName}: ${formatMetricValue(metric.value, value)} (${formatMetricCountLabel(metric.value, count)})`
                : `${seriesName}: ${formatMetricValue(metric.value, value)}`,
            ],
          };
        }}
        dataSeries={dataSeries}
      />
    </div>
  );
  return graphContainer(content, metric.label, metric.description);
}

OperationalMetricWidget.propTypes = {
  metric: PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
  }).isRequired,
  operationalFacts: PropTypes.object,
  colorPalette: PropTypes.object,
  config: PropTypes.object,
  groupBy: PropTypes.oneOf(['none', 'client', 'partner', 'provider']),
};

export const buildOperationalWidgets = ({ operationalFacts, colorPalette, configuration, isLoading, groupBy }) =>
  getOperationalWidgetMetadata().map(metadata => ({
    ...metadata,
    noPadding: true,
    greyBackground: false,
    render: (
      isTableMetric(metadata.id) ? (
        <BulkOrdersTableWidget
          metric={getMetricDefinition(metadata.id)}
          operationalFacts={operationalFacts}
        />
      ) : (
        <OperationalMetricWidget
          metric={getMetricDefinition(metadata.id)}
          operationalFacts={operationalFacts}
          colorPalette={colorPalette}
          config={configuration?.[metadata.id]}
          groupBy={groupBy}
        />
      )
    ),
    loading: isLoading,
  }));

export const buildPerformanceWidgets = ({ operationalFacts, colorPalette, configuration, isLoading, groupBy }) =>
  getPerformanceWidgetMetadata().map(metadata => ({
    ...metadata,
    noPadding: true,
    greyBackground: false,
    render: (
      <OperationalMetricWidget
        metric={getMetricDefinition(metadata.id)}
        operationalFacts={operationalFacts}
        colorPalette={colorPalette}
        config={configuration?.[metadata.id]}
        groupBy={groupBy}
      />
    ),
    loading: isLoading,
  }));

function OperationalReportTab({
  baseRange,
  groupBy,
  periodStart,
  onBaseRangeChange,
  onGroupByChange,
  onShiftPeriod,
}) {
  const periodLabel = useMemo(() => {
    const start = moment(periodStart);
    const end = moment(periodStart).add(1, baseRange).subtract(1, 'day');
    return `${start.format('MMM D, YYYY')} - ${end.format('MMM D, YYYY')}`;
  }, [baseRange, periodStart]);

  return (
    <div className="report-operational-tab">
      <div className="report-operational-toolbar">
        <div className="report-operational-nav">
          <button type="button" className="report-operational-button" onClick={() => onShiftPeriod(-1)}>
            Previous
          </button>
          <div className="report-operational-period">{periodLabel}</div>
          <button type="button" className="report-operational-button" onClick={() => onShiftPeriod(1)}>
            Next
          </button>
        </div>
        <div className="report-operational-selects">
          <Select
            id="operational-group-by"
            label="Group by"
            className="report-summary-select"
            data={GROUP_BY_OPTIONS}
            value={groupBy}
            onChange={option => onGroupByChange(option.value)}
          />
          <Select
            id="operational-base-range"
            label="Time range"
            className="report-summary-select"
            data={BASE_RANGE_OPTIONS}
            value={baseRange}
            onChange={option => onBaseRangeChange(option.value)}
          />
        </div>
      </div>
    </div>
  );
}

OperationalReportTab.propTypes = {
  baseRange: PropTypes.string.isRequired,
  groupBy: PropTypes.oneOf(['none', 'client', 'partner', 'provider']).isRequired,
  periodStart: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  onBaseRangeChange: PropTypes.func.isRequired,
  onGroupByChange: PropTypes.func.isRequired,
  onShiftPeriod: PropTypes.func.isRequired,
};

export default OperationalReportTab;
